import React from 'react';
import { Star, Crown, MapPin, Clock, Heart, Navigation } from 'lucide-react';
import { useI18n } from '../i18n';
import { useApp } from '../context/AppContext';

const OfficeCard = ({ office, onClick, layout = 'card', showFavorite = true }) => {
  const { toggleFavorite: toggleFav, isFavorite: checkFavorite } = useApp();
  const { t } = useI18n();
  const isFavorite = checkFavorite(office.id, 'offices');

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    toggleFav(office.id, 'offices');
  };

  if (layout === 'list') {
    return (
      <div
        onClick={onClick}
        className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-100 hover:border-emerald-200 hover:shadow-md transition-all cursor-pointer"
      >
        <div className="relative">
          <img src={office.image} alt={office.name} className="w-20 h-20 rounded-xl object-cover" />
          {office.isElit && (
            <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center">
              <Crown className="w-3.5 h-3.5 text-white" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{office.name}</h3>
          <p className="text-sm text-gray-500">{office.category}</p>
          {office.address && (
            <p className="text-xs text-gray-400 truncate mt-0.5" title={office.address}>{office.address}</p>
          )}
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              {office.rating}
            </span>
            {office.distance && (
              <span className="flex items-center gap-1 text-emerald-600">
                <Navigation className="w-4 h-4" />
                {office.distance}
              </span>
            )}
          </div>
        </div>
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-emerald-200 transition-all cursor-pointer group"
    >
      <div className="relative h-40 sm:h-48">
        <img 
          src={office.image} 
          alt={office.name} 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {office.isElit && (
            <div className="flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg">
              <Crown className="w-3.5 h-3.5" />
              ELIT
            </div>
          )}
          {/* Open/Closed badge */}
          <div className={`text-xs font-medium px-2.5 py-1 rounded-full shadow-lg ${
            office.isOpen !== false ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
          }`}>
            {office.isOpen !== false ? t('office', 'open') : t('office', 'closed')}
          </div>
        </div>

        {/* Favorite button */}
        {showFavorite && (
          <button 
            onClick={handleToggleFavorite}
            className="absolute top-3 right-3 w-9 h-9 bg-white/90 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg"
            data-testid={`favorite-btn-${office.id}`}
          >
            <Heart className={`w-5 h-5 transition-colors ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-600'}`} />
          </button>
        )}

        {/* Price range */}
        <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur text-gray-700 text-xs font-medium px-2 py-1 rounded-full">
          {office.priceRange}
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{office.name}</h3>
            <p className="text-sm text-gray-500 truncate">{office.category}</p>
          </div>
          <div className="flex items-center gap-1 bg-emerald-50 px-2 py-1 rounded-lg flex-shrink-0">
            <Star className="w-4 h-4 text-emerald-500 fill-emerald-500" />
            <span className="font-semibold text-emerald-600 text-sm">{office.rating}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
          {office.address && (
            <span className="flex items-center gap-1 truncate" title={office.address}>
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{office.address}</span>
            </span>
          )}
          {office.distance && (
            <span className="flex items-center gap-1 text-emerald-600 flex-shrink-0">
              <Navigation className="w-4 h-4" />
              {office.distance}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfficeCard;
